import { useState, useEffect } from "react";
import { formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Reply, User, Clock, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import type { Database } from "@/integrations/supabase/types";

type Message = Database["public"]["Tables"]["messages"]["Row"];

interface MessageThreadProps {
  message: Message;
}

export const MessageThread = ({ message }: MessageThreadProps) => {
  const [replyContent, setReplyContent] = useState("");
  const [replying, setReplying] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);

  const getPriorityColor = (priority: string | null) => {
    switch (priority) {
      case 'high':
        return 'destructive';
      case 'medium':
        return 'default';
      default:
        return 'secondary';
    }
  };

  const getMessageTypeColor = (type: string) => {
    switch (type) {
      case 'urgent':
        return 'destructive';
      case 'certification_request':
        return 'default';
      case 'project_inquiry':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim()) return;

    setReplying(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("messages")
        .insert({
          sender_id: user.id,
          recipient_id: message.sender_id,
          subject: `Re: ${message.subject}`,
          content: replyContent,
          thread_id: message.thread_id || message.id,
          project_id: message.project_id,
          message_type: 'user_message',
          priority: 'normal',
        });

      if (error) throw error;

      toast.success("Reply sent successfully");
      setReplyContent("");
      setShowReplyForm(false);
    } catch (error) {
      console.error("Error sending reply:", error);
      toast.error("Failed to send reply");
    } finally {
      setReplying(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <CardTitle className="text-lg">{message.subject}</CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant={getMessageTypeColor(message.message_type)}>
                {message.message_type.replace('_', ' ')}
              </Badge>
              {message.priority && (
                <Badge variant={getPriorityColor(message.priority)}>
                  {message.priority} priority
                </Badge>
              )}
              {message.project_id && (
                <Badge variant="outline">
                  Project Message
                </Badge>
              )}
            </div>
          </div>
          
          <div className="text-right text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Message sender info */}
        <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
          <Avatar className="h-8 w-8">
            <AvatarFallback>
              <User className="h-4 w-4" />
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="text-sm font-medium">System User</p>
            <p className="text-xs text-muted-foreground">
              {new Date(message.created_at).toLocaleString()}
            </p>
          </div>
        </div>

        {/* Message content */}
        <div className="prose prose-sm max-w-none">
          <div className="p-4 bg-background border rounded-lg">
            <p className="whitespace-pre-wrap text-sm leading-relaxed">
              {message.content}
            </p>
          </div>
        </div>

        {/* Status and metadata */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            Status: <Badge variant="outline" className="text-xs">
              {message.status}
            </Badge>
          </div>
          {message.read_at && (
            <div>
              Read: {formatDistanceToNow(new Date(message.read_at), { addSuffix: true })}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-4 border-t">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowReplyForm(!showReplyForm)}
            className="flex items-center gap-2"
          >
            <Reply className="h-4 w-4" />
            Reply
          </Button>
        </div>

        {/* Reply form */}
        {showReplyForm && (
          <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
            <Textarea
              placeholder="Type your reply..."
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              className="min-h-24"
            />
            <div className="flex items-center gap-2">
              <Button 
                onClick={handleReply}
                disabled={replying || !replyContent.trim()}
                size="sm"
              >
                {replying ? "Sending..." : "Send Reply"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowReplyForm(false);
                  setReplyContent("");
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};